import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import LikeTabV4 from './LikeTabV4';
import PurchaseTabV4 from './PurchaseTabV4';
import RecentTabV4 from './RecentTabV4';
import {Page} from '../../../common/constant/page';
import {HiddenBottomButtonContext} from '../../../common/context';
import {getThemeOnBody} from '../../../common/function';
import useFetchNextList from '../../../common/hook/useFetchNextList';
import useFetchTabV4 from '../../../common/hook/useFetchTabV4';
import useOnWindowScroll from '../../../common/hook/useOnWindowScroll';
import useTypedSelector from '../../../common/hook/useTypedSelector';
import {routeTo} from '../../../common/util/PageRouter';
import {
  getLocalStorageObject,
  setLocalStorageObject,
} from '../../../common/util/storage';
import {sendPageView} from '../../../common/util/tracking';
import {onScrollGnb} from '../../common/common';
import HomeFooter from '../../common/component/HomeFooter';
import {sendHomeEvent} from '../../common/tracking';
import {HomeTabPage} from '../../common/type';
import {MY_PAGE_VIEW_PARAMS} from '../common';
import MyPageBottomButtonsV4 from '../component/MyPageBottomButtonsV4';
import useMyPageTabInfo from '../hook/useMyPageTabInfo';
import {actions, ActionType} from '../state/action';
import {IClickContentHandler, IMyPageSecondTab, ISortV4} from '../type';

export default function MyPageV4() {
  const tab = useTypedSelector(
    ({home}) => home.secondTabs[HomeTabPage.MyPage].selected
  );
  const {
    isRecentTab,
    isLikeTab,
    isPurchaseTab,
    isEmpty,
    isFirstLoading,
    isLogin,
    isLoginChecking,
    showOnlyLocalList,
    recent,
    like,
    purchase,
    setOnlyLocalList,
  } = useMyPageTabInfo({tab, actionType: ACTION_TYPE});

  const dispatch = useDispatch();
  const [currentSort, sortList] = useTypedSelector(state => [
    state.myPage.tab[tab]?.currentSort,
    state.myPage.tab[tab]?.sortList,
  ]);

  const {t} = useTranslation();
  const currentSortIndex =
    sortList?.findIndex(sort => sort.sortKey === currentSort) || 0;
  const currentSortLabel = sortList
    ? t(sortList[currentSortIndex]?.label)
    : undefined;

  const handleClickContent: IClickContentHandler = ({id, sid}, index) => {
    sendHomeEvent({
      actionName: 'content',
      action: 'click',
      customProps: {
        content_id: id,
        ordnum: index + 1,
        event_layer: isRecentTab && 'section',
      },
    });
    routeTo({page: Page.Content, id, sid});
  };

  const handleClickEditButton = () => {
    sendHomeEvent({
      actionName: 'content_edit',
      action: 'click',
      customProps: {
        event_layer: 'bottom',
      },
    });
    routeTo(
      {page: Page.MyPageItemList, tab},
      {isReplace: false, isShallow: false}
    );
  };

  const handleClickSort = () => {
    if (!sortList) return;

    const lastIndex = sortList.length - 1;
    const nextIndex = currentSortIndex + 1;
    const sort = {
      currentSort: sortList[nextIndex > lastIndex ? 0 : nextIndex].sortKey,
      sortList,
    };
    const sortingOption = sort.currentSort.includes('UPDATE')
      ? 'update'
      : MY_PAGE_VIEW_PARAMS[tab].actionNameAndSort;
    !isFirstLoading && dispatch(actions.fetchTabSort({tab, sort}));
    setLocalStorageObject({
      key: MY_PAGE_SORT_LS_MAP[tab],
      value: sort,
    });
    sendHomeEvent({
      actionName: 'sorting_option',
      action: 'click',
      customProps: {
        event_layer: 'bottom',
        sorting_option: sortingOption,
      },
    });
    sendPageView({
      section: 'library',
      page: MY_PAGE_VIEW_PARAMS[tab].page,
      actionName: MY_PAGE_VIEW_PARAMS[tab].actionNameAndSort,
      customProps: {
        display_theme: getThemeOnBody(),
        sorting_option: sortingOption,
      },
      isSendExtra: true,
    });
  };

  const fetchTabItems = (isFirst?: boolean) => {
    isLogin &&
      dispatch(
        actions.fetchTabItems(
          {
            tab,
            sortInfo: getLocalStorageObject({
              key: MY_PAGE_SORT_LS_MAP[tab],
            }) as Required<ISortV4>,
          },
          isFirst
        )
      );
  };

  const {NextLoading} = useFetchNextList({
    actionType: ACTION_TYPE,
    fetchKey: tab,
    isEnd: isEmpty,
    disabled: showOnlyLocalList || isFirstLoading || isRecentTab,
    requestGetNextList: fetchTabItems,
  });

  useFetchTabV4({
    onFetch: () => {
      fetchTabItems(true);
      showOnlyLocalList && setOnlyLocalList();
    },
    disabled: isLoginChecking,
    changeTab: tab,
  });

  useEffect(() => {
    const sort = getLocalStorageObject({
      key: MY_PAGE_SORT_LS_MAP[tab],
    }) as Required<ISortV4>;

    const sortingOption =
      sort?.currentSort.includes('UPDATE') || (!sort && tab === 'like')
        ? 'update'
        : MY_PAGE_VIEW_PARAMS[tab].actionNameAndSort;

    sendPageView({
      section: 'library',
      page: MY_PAGE_VIEW_PARAMS[tab].page,
      actionName: MY_PAGE_VIEW_PARAMS[tab].actionNameAndSort,
      customProps: {
        display_theme: getThemeOnBody(),
        sorting_option: sortingOption,
      },
      isSendExtra: true,
    });
  }, [tab]);

  const [isHiddenBottomButton, setIsHiddenBottomButton] = useState(false);
  useOnWindowScroll({
    onScroll: scrollTop => {
      onScrollGnb(scrollTop, isHide => {
        setIsHiddenBottomButton(isHide);
      });
    },
  });

  return (
    <HiddenBottomButtonContext.Provider value={isHiddenBottomButton}>
      {isRecentTab && (
        <RecentTabV4
          recent={recent}
          showLoading={isFirstLoading}
          onClickContent={handleClickContent}
        />
      )}
      {isLikeTab && !isLoginChecking && (
        <LikeTabV4
          like={like}
          showLoading={isFirstLoading}
          onClickContent={handleClickContent}
        />
      )}
      {isPurchaseTab && !isLoginChecking && (
        <PurchaseTabV4
          purchase={purchase}
          showLoading={isFirstLoading}
          onClickContent={handleClickContent}
        />
      )}
      <NextLoading />
      {!isFirstLoading && (
        <HomeFooter
          isBottomFixed={isRecentTab ? isEmpty : isEmpty || !isLogin}
        />
      )}
      {!isEmpty && (isRecentTab || (!isRecentTab && currentSortLabel)) && (
        <MyPageBottomButtonsV4
          isHideSortButton={showOnlyLocalList}
          currentSortLabel={currentSortLabel}
          onClickEditButton={handleClickEditButton}
          onClickSort={handleClickSort}
        />
      )}
    </HiddenBottomButtonContext.Provider>
  );
}

const ACTION_TYPE = ActionType.FetchTabItems;

const MY_PAGE_SORT_LS_MAP: {[key in IMyPageSecondTab]: string} = {
  recent: 'my-page-recent-sort',
  like: 'my-page-like-sort',
  own: 'my-page-purchase-sort',
};
